import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Input, Modal, Select, Text } from '@viapeople/components'
import { useCallback, useState } from 'react'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'

const KEY_ALGORITHM_OPTIONS = [
	{ value: 'ed25519', label: 'Ed25519 (recommended)' },
	{ value: 'rsa', label: 'RSA 4096-bit (legacy compatibility)' },
]

interface GenerateKeyDialogProps {
	isOpen: boolean
	onClose: () => void
	organizationId: string
}

export function GenerateKeyDialog({ isOpen, onClose, organizationId }: GenerateKeyDialogProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const [label, setLabel] = useState('')
	const [keyAlgorithm, setKeyAlgorithm] = useState<'ed25519' | 'rsa'>('ed25519')
	const [generated, setGenerated] = useState<{
		fingerprint: string
		connectionInfo: { host: string; port: number; username: string }
	} | null>(null)

	const generateMutation = useMutation({
		...trpc.admin.sftp.generateKey.mutationOptions({
			onSuccess: (data) => {
				// Trigger browser download of private key
				const blob = new Blob([data.privateKeyPem], { type: 'application/x-pem-file' })
				const url = URL.createObjectURL(blob)
				const a = document.createElement('a')
				a.href = url
				a.download = `sftp-key${data.label ? `-${data.label}` : ''}.pem`
				document.body.appendChild(a)
				a.click()
				document.body.removeChild(a)
				URL.revokeObjectURL(url)

				// Store display info before clearing mutation data
				setGenerated({
					fingerprint: data.fingerprint,
					connectionInfo: data.connectionInfo,
				})

				// Clear private key from React memory
				generateMutation.reset()

				queryClient.invalidateQueries({
					queryKey: trpc.admin.sftp.listKeys.queryKey({ organizationId }),
				})
			},
			onError: (error) => {
				showToast(`Error generating key: ${error.message}`, 'error')
			},
		}),
		gcTime: 0,
	})

	const handleGenerate = useCallback(() => {
		generateMutation.mutate({ organizationId, label: label || undefined, keyAlgorithm })
	}, [generateMutation, organizationId, label, keyAlgorithm])

	const handleClose = useCallback(() => {
		setLabel('')
		setKeyAlgorithm('ed25519')
		setGenerated(null)
		onClose()
	}, [onClose])

	const handleCopyConnectionInfo = () => {
		if (!generated?.connectionInfo) return
		const { host, port, username } = generated.connectionInfo
		const instructions = `SFTP Connection Details:\nHost: ${host}\nPort: ${port}\nUsername: ${username}\n\nConnect using:\nsftp -i <path-to-private-key> ${username}@${host}`
		navigator.clipboard.writeText(instructions).then(
			() => showToast('Connection instructions copied to clipboard', 'info'),
			() => showToast('Failed to copy to clipboard. Please copy manually.', 'error'),
		)
	}

	if (generated) {
		return (
			<Modal
				isOpen={isOpen}
				onClose={handleClose}
				title='Key Generated Successfully'
				buttons={[{ children: 'Done', onClick: handleClose, variant: 'primary' }]}>
				<div className='space-y-4'>
					<div className='bg-success-50 border border-success-200 rounded-lg p-3'>
						<Text variant='small' weight='semibold' className='text-success-800'>
							Private key downloaded successfully.
						</Text>
						<Text variant='small' className='text-success-700 mt-1'>
							Store it securely. It cannot be retrieved again.
						</Text>
					</div>

					<div>
						<Text variant='small' className='text-neutral-500'>
							Fingerprint
						</Text>
						<Text variant='small' weight='semibold' className='font-mono'>
							{generated.fingerprint}
						</Text>
					</div>

					<div className='bg-neutral-50 border border-neutral-200 rounded-lg p-3'>
						<Text variant='small' weight='semibold' className='mb-2'>
							Connection Details
						</Text>
						<div className='space-y-1'>
							<Text variant='small'>
								Host: <span className='font-mono'>{generated.connectionInfo.host}</span>
							</Text>
							<Text variant='small'>
								Port: <span className='font-mono'>{generated.connectionInfo.port}</span>
							</Text>
							<Text variant='small'>
								Username: <span className='font-mono'>{generated.connectionInfo.username}</span>
							</Text>
						</div>
					</div>

					<button
						type='button'
						className='text-sm text-primary-700 hover:text-primary-800 font-medium'
						onClick={handleCopyConnectionInfo}>
						Copy Connection Instructions
					</button>
				</div>
			</Modal>
		)
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title='Generate SSH Key'
			buttons={[
				{ children: 'Cancel', onClick: handleClose, variant: 'secondary', disabled: generateMutation.isPending },
				{
					children: generateMutation.isPending ? 'Generating...' : 'Generate',
					onClick: handleGenerate,
					variant: 'primary',
					disabled: generateMutation.isPending,
				},
			]}>
			<div className='space-y-4'>
				<div className='bg-warning-50 border border-warning-200 rounded-lg p-3'>
					<Text variant='small' className='text-warning-800'>
						The private key will download once and cannot be retrieved again. Make sure to save it securely.
					</Text>
				</div>

				<div>
					<Text variant='small' weight='semibold' className='mb-1'>
						Key Type
					</Text>
					<Select
						options={KEY_ALGORITHM_OPTIONS}
						value={keyAlgorithm}
						onChange={(value) => setKeyAlgorithm(value as 'ed25519' | 'rsa')}
					/>
					<Text variant='small' className='text-neutral-500 mt-1'>
						{keyAlgorithm === 'rsa'
							? 'Use RSA for legacy systems like GoAnywhere MFT or older SFTP clients.'
							: 'Ed25519 is faster and more secure. Use this unless your SFTP client requires RSA.'}
					</Text>
				</div>

				<Input
					label='Label (optional)'
					value={label}
					onChange={(e) => setLabel(e.target.value)}
					placeholder='e.g., Production key, CI/CD pipeline'
					hint='A friendly name to identify this key'
				/>
			</div>
		</Modal>
	)
}
